import {
  DeserializedRecord,
  deserializeItem,
  deserializeManyRelationship,
  Deserializer,
  deserializeSingleRelationship,
  JSONAPIManyRelationship,
  JSONAPIRecord,
  JSONAPISingleRelationship
} from "./Helpers";
import {Tag} from "./Tag";
import {deserializePerson, Person} from "./Person";
import {deserializeRestrictionIcd10, RestrictionIcd10} from "./restriction/icd10";
import {deserializeRestrictionManual, RestrictionManual} from "./restriction/Manual";
import {Assignment as ComponentAssignment, deserializeComponentAssignment} from "./program/component/Assignment";
import {deserializeVersion, Version} from "./Version";
import {deserializeProgramPhase, ProgramPhase} from "./program/Phase";
import {ProgramReportTemplate} from "./program/ProgramReportTemplate";

interface Program extends DeserializedRecord<"program"> {
  title: string;
  description?: string;
  externalId?: string;
  goal?: string;
  maxDurationInMinutes?: number;
  minDurationInMinutes?: number;
  remark?: string;
  targetGroup?: string;
  tags: Tag[];
  programTagsAttributes: ProgramTag[];
  personAssignments: PersonAssignment[];
  restrictionIcd10s: RestrictionIcd10[];
  restrictionManuals: RestrictionIcd10[];
  programComponentAssignments: ComponentAssignment[];
  programReportTemplate?: ProgramReportTemplate;
  programPhases: ProgramPhase[];
  versions: Version<Program>[];
  updatePatientProgramPhases?: boolean;
}

type ProgramPersonContactType = "technical" | "organizational";
type NewProgramPerson = Pick<PersonAssignment, 'person' | 'contactType'>;

interface PersonAssignment extends DeserializedRecord {
  contactType: ProgramPersonContactType
  program: Program
  person: Person
}

interface PersonAssignmentData extends JSONAPIRecord {
  relationships: {
    person: JSONAPISingleRelationship
  }
}

interface ProgramTag extends DeserializedRecord {
  tag: Tag
}

interface ProgramTagData extends JSONAPIRecord {
  relationships: {
    tag: JSONAPISingleRelationship
  }
}

interface ProgramData extends JSONAPIRecord {
  relationships: {
    tags: JSONAPIManyRelationship,
    program_tags: JSONAPIManyRelationship,
    person_assignments: JSONAPIManyRelationship,
    restriction_icd10s: JSONAPIManyRelationship,
    restriction_manuals: JSONAPIManyRelationship,
    program_component_assignments: JSONAPIManyRelationship,
    program_phases: JSONAPIManyRelationship,
    program_report_template: JSONAPISingleRelationship,
    versions: JSONAPIManyRelationship
  }
}

const deserializePeopleAssignments: Deserializer<PersonAssignment, PersonAssignmentData> = (data, included) => {
  const person = deserializeSingleRelationship<Person>(data.relationships.person, included, deserializePerson);

  return {
    ...deserializeItem(data),
    person: person
  };
};

const deserializeProgramTag: Deserializer<ProgramTag, ProgramTagData> = (data, included) => {
  const tag = deserializeSingleRelationship<Tag>(data.relationships.tag, included);

  return {
    tag: tag,
    tagId: data.relationships.tag.data.id,
    ...deserializeItem(data)
  };
};

const deserializeProgram: Deserializer<Program, ProgramData> = (data, included) => {
  const programTags = deserializeManyRelationship<ProgramTag>(data.relationships.program_tags, included, deserializeProgramTag);
  const tags = programTags.map((t) => t.tag);
  // eslint-disable-next-line @stylistic/max-len
  const personAssignments = deserializeManyRelationship<PersonAssignment>(data.relationships.person_assignments, included, deserializePeopleAssignments);
  const restrictionIcd10s = deserializeManyRelationship(data.relationships.restriction_icd10s, included, deserializeRestrictionIcd10);
  // eslint-disable-next-line @stylistic/max-len
  const restrictionManuals = deserializeManyRelationship<RestrictionManual>(data.relationships.restriction_manuals, included, deserializeRestrictionManual);
  // eslint-disable-next-line @stylistic/max-len
  const programComponentAssignments = deserializeManyRelationship<ComponentAssignment>(data.relationships.program_component_assignments, included, deserializeComponentAssignment);
  const programPhases = deserializeManyRelationship<ProgramPhase>(data.relationships.program_phases, included, deserializeProgramPhase);
  const versions = deserializeManyRelationship(data.relationships.versions, included, deserializeVersion as Deserializer<Version<Program>>);
  const programReportTemplate = deserializeSingleRelationship<ProgramReportTemplate>(data.relationships.program_report_template, included);

  return ({
    tags,
    personAssignments,
    restrictionIcd10s,
    restrictionManuals,
    programPhases,
    programComponentAssignments,
    programReportTemplate,
    programTagsAttributes: programTags,
    versions,
    ...deserializeItem(data)
  });
};

export type {
  Program, PersonAssignment, NewProgramPerson, ProgramPersonContactType, PersonAssignmentData
};

export {
  deserializeProgram, deserializePeopleAssignments
};
